import React, { useEffect, useRef } from 'react';
import { CloseOutline, SendOutline } from 'react-ionicons';

import { useChat } from '../../contexts/ChatContext';
import { useConciergeChat } from '../../hooks/useConciergeChat';
import { useIsMobile } from '../../hooks/useIsMobile';
import { FILLS, FONTS, GLASS, GRADIENTS, RADII, SHADOWS, STROKES } from '../../styles/tokens';

const SHEET_TOP = 60;
const AVATAR_SIZE = 40;
const CLOSE_SIZE = 32;
const SEND_SIZE = 40;
const ICON_SIZE = 20;

/**
 * Shared message-bubble style. User bubbles are the gold fill with page-coloured
 * text; bot bubbles use the neutral chip fill — matching the desktop
 * `ConciergePanel` exactly so the two surfaces read identically.
 */
const bubbleStyle = (sender: 'user' | 'bot'): React.CSSProperties => ({
  alignSelf: sender === 'user' ? 'flex-end' : 'flex-start',
  maxWidth: '82%',
  padding: '12px 14px',
  borderRadius: '14px',
  borderBottomLeftRadius: sender === 'bot' ? '4px' : '14px',
  borderBottomRightRadius: sender === 'user' ? '4px' : '14px',
  fontSize: '14px',
  lineHeight: 1.5,
  fontFamily: FONTS.body,
  background: sender === 'user' ? FILLS.gold : FILLS.chip,
  color: sender === 'user' ? FILLS.page : FILLS.textLight,
  fontWeight: sender === 'user' ? 500 : 400,
  whiteSpace: 'pre-wrap',
});

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: FILLS.overlayHeavy,
  zIndex: 1190,
};

const sheetStyle: React.CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  top: SHEET_TOP,
  background: FILLS.page,
  borderTopLeftRadius: '20px',
  borderTopRightRadius: '20px',
  borderTop: `1px solid ${STROKES.goldCard}`,
  boxShadow: SHADOWS.chatPanel,
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1200,
};

const avatarStyle: React.CSSProperties = {
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  borderRadius: RADII.pill,
  background: GRADIENTS.goldButton,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const closeButtonStyle: React.CSSProperties = {
  width: CLOSE_SIZE,
  height: CLOSE_SIZE,
  borderRadius: RADII.pill,
  background: FILLS.surface,
  border: `1px solid ${STROKES.subtle}`,
  color: FILLS.textLight,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  padding: 0,
};

/**
 * Mobile concierge bottom sheet. Visible only when the chat is open
 * (`isChatOpen` from `useChat`) and the viewport is mobile (`useIsMobile`).
 * Drives the conversation through the shared `useConciergeChat` hook, so the
 * messages, send flow, suggestion chips and error handling are identical to the
 * desktop `ConciergePanel`. A backdrop and a header close button both call
 * `closeChat`.
 */
const MobileConciergeSheet: React.FC = () => {
  const isMobile = useIsMobile();
  const { isChatOpen, closeChat } = useChat();
  const { messages, input, setInput, isTyping, handleSend, suggestionChips, showSuggestions } =
    useConciergeChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  if (!isMobile || !isChatOpen) return null;

  return (
    <>
      <div style={backdropStyle} data-testid="concierge-sheet-backdrop" onClick={closeChat} />

      <div style={sheetStyle} role="dialog" aria-label="Concierge">
        {/* Grab handle */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '10px',
            paddingBottom: '6px',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: '40px',
              height: '4px',
              borderRadius: '4px',
              background: STROKES.subtle,
            }}
          />
        </div>

        {/* Header */}
        <div
          style={{
            padding: '8px 20px 14px',
            borderBottom: `1px solid ${STROKES.subtle}`,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexShrink: 0,
          }}
        >
          <div style={avatarStyle}>
            <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M21 15C21 16.1 20.1 17 19 17H7L3 21V5C3 3.9 3.9 3 5 3H19C20.1 3 21 3.9 21 5V15Z"
                stroke={FILLS.page}
                strokeWidth="1.8"
                fill="none"
                strokeLinejoin="round"
              />
              <circle cx="8" cy="10" r="1" fill={FILLS.page} />
              <circle cx="12" cy="10" r="1" fill={FILLS.page} />
              <circle cx="16" cy="10" r="1" fill={FILLS.page} />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: FONTS.heading,
                fontSize: '18px',
                fontWeight: 600,
                lineHeight: 1.1,
                color: FILLS.textPrimary,
              }}
            >
              Concierge
            </div>
            <div
              style={{
                fontSize: '11.5px',
                color: FILLS.textMuted,
                marginTop: '2px',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                fontFamily: FONTS.body,
              }}
            >
              <span
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: RADII.pill,
                  background: FILLS.success,
                }}
              />
              Available 24/7
            </div>
          </div>
          <button
            type="button"
            onClick={closeChat}
            aria-label="Close concierge"
            style={closeButtonStyle}
          >
            <CloseOutline width="12px" height="12px" color="currentColor" />
          </button>
        </div>

        {/* Messages */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '18px 18px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {messages.map((msg) => (
            <div key={msg.id} style={bubbleStyle(msg.sender)}>
              {msg.text}
            </div>
          ))}

          {isTyping && (
            <div
              style={{
                ...bubbleStyle('bot'),
                color: FILLS.textMuted,
              }}
            >
              Thinking...
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestion chips */}
        {showSuggestions && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              padding: '0 18px 12px',
              flexShrink: 0,
            }}
          >
            {suggestionChips.map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => handleSend(chip)}
                style={{
                  padding: '7px 14px',
                  borderRadius: RADII.pill,
                  border: `1px solid ${STROKES.subtle}`,
                  background: GLASS.chat.background,
                  color: FILLS.textMuted,
                  fontFamily: FONTS.body,
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                {chip}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div
          style={{
            padding: '10px 14px calc(18px + env(safe-area-inset-bottom))',
            borderTop: `1px solid ${STROKES.subtle}`,
            background: FILLS.page,
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Message your concierge…"
            style={{
              flex: 1,
              background: FILLS.input,
              border: `1px solid ${STROKES.subtle}`,
              borderRadius: RADII.pill,
              padding: '11px 16px',
              color: FILLS.textPrimary,
              fontFamily: FONTS.body,
              fontSize: '13.5px',
              outline: 'none',
            }}
          />
          <button
            type="button"
            onClick={() => handleSend()}
            disabled={!input.trim()}
            aria-label="Send message"
            style={{
              width: SEND_SIZE,
              height: SEND_SIZE,
              borderRadius: RADII.pill,
              background: input.trim() ? GRADIENTS.goldButton : FILLS.chip,
              border: 'none',
              cursor: input.trim() ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              padding: 0,
            }}
          >
            <SendOutline
              width="16px"
              height="16px"
              color={input.trim() ? FILLS.page : FILLS.textMuted}
            />
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileConciergeSheet;
